import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import { 
  CheckCircle, XCircle, Clock, AlertCircle, Trophy, 
  Calendar, GraduationCap, PartyPopper, Loader2
} from 'lucide-react';

const DecisionTracker = () => {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    loadDecisions();
  }, []);

  const loadDecisions = async () => {
    try {
      const response = await api.get('/decisions/all');
      setDecisions(response.data.decisions || []);
    } catch (error) {
      console.error('Error loading decisions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSimulateDecision = async (applicationId, status) => {
    try {
      await api.post(`/decisions/application/${applicationId}/simulate`, { status });
      toast({ title: 'Decision Updated', description: `Status changed to ${status}` });
      loadDecisions();
    } catch (error) {
      // Global error handler shows toast
    }
  };

  const handleConfirmEnrollment = async (applicationId) => {
    try {
      await api.post(`/decisions/application/${applicationId}/confirm-enrollment`, {});
      toast({ title: 'Congratulations!', description: 'Enrollment confirmed!' });
      loadDecisions();
    } catch (error) {
      // Global error handler shows toast
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'denied': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'waitlisted': return <Clock className="h-5 w-5 text-amber-500" />;
      case 'deferred': return <AlertCircle className="h-5 w-5 text-blue-500" />;
      case 'pending': return <Clock className="h-5 w-5 text-gray-400" />;
      case 'withdrawn': return <XCircle className="h-5 w-5 text-gray-400" />;
      default: return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      accepted: 'bg-green-100 text-green-800 border-green-300',
      denied: 'bg-red-100 text-red-800 border-red-300',
      waitlisted: 'bg-amber-100 text-amber-800 border-amber-300',
      deferred: 'bg-blue-100 text-blue-800 border-blue-300',
      pending: 'bg-gray-100 text-gray-800 border-gray-300',
      withdrawn: 'bg-gray-100 text-gray-500 border-gray-300'
    };
    return styles[status] || styles.pending;
  };

  const filteredDecisions = activeTab === 'all' 
    ? decisions 
    : decisions.filter(d => d.decision_status === activeTab);

  const stats = {
    total: decisions.length,
    accepted: decisions.filter(d => d.decision_status === 'accepted').length,
    pending: decisions.filter(d => d.decision_status === 'pending').length,
    waitlisted: decisions.filter(d => d.decision_status === 'waitlisted').length,
    denied: decisions.filter(d => d.decision_status === 'denied').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Decision Tracker</h1>
          <p className="text-gray-600">Track your application decisions in one place</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-600">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-green-600">{stats.accepted}</p>
              <p className="text-sm text-green-700">Accepted</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-gray-600">{stats.pending}</p>
              <p className="text-sm text-gray-600">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{stats.waitlisted}</p>
              <p className="text-sm text-amber-700">Waitlisted</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-red-600">{stats.denied}</p>
              <p className="text-sm text-red-700">Denied</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="accepted">Accepted ({stats.accepted})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
            <TabsTrigger value="waitlisted">Waitlisted ({stats.waitlisted})</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {filteredDecisions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <GraduationCap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No applications in this category</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredDecisions.map((decision) => (
                  <Card key={decision.application_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {getStatusIcon(decision.decision_status)}
                          <div>
                            <h3 className="text-lg font-semibold">{decision.college_name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">{decision.deadline_type}</Badge>
                              <Badge className={getStatusBadge(decision.decision_status)}>
                                {decision.decision_status.charAt(0).toUpperCase() + decision.decision_status.slice(1)}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {decision.decision_status === 'accepted' && (
                            <Button 
                              onClick={() => handleConfirmEnrollment(decision.application_id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <PartyPopper className="h-4 w-4 mr-2" />
                              Confirm Enrollment
                            </Button>
                          )}
                          
                          {decision.decision_status === 'pending' && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" onClick={() => handleSimulateDecision(decision.application_id, 'accepted')}>
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleSimulateDecision(decision.application_id, 'waitlisted')}>
                                <Clock className="h-4 w-4 text-amber-500" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleSimulateDecision(decision.application_id, 'denied')}>
                                <XCircle className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {decision.decision_date && (
                        <p className="text-sm text-gray-500 mt-2">
                          <Calendar className="h-4 w-4 inline mr-1" />
                          Decision received: {new Date(decision.decision_date).toLocaleDateString()}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DecisionTracker;
